import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import styled from 'styled-components';

const FormContainer = styled.div`
  max-width: 400px;
  margin: 2rem auto;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadow};
  background-color: ${props => props.theme.cardBg};
  transition: all 0.3s ease;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.primaryText};
  transition: color 0.3s ease;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 0.25rem;
  font-size: 1rem;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.primaryText};
  transition: all 0.3s ease;
  
  &:focus {
    border-color: ${props => props.theme.inputFocusBorder};
    outline: none;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: #3182ce;
  }
  
  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  margin-top: 1rem;
  text-align: center;
  font-size: 0.875rem;
  transition: color 0.3s ease;
`;

const SignupLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.875rem;
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ForgotLink = styled.div`
  text-align: right;
  margin-top: -0.35rem;
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 500;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const LoginForm = () => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }
    
    setIsSubmitting(true);
    const success = await login(email, password);
    
    if (success) {
      const from = location.state && location.state.from ? location.state.from : null;
      if (from && from !== '/login') {
        navigate(from, { replace: true });
      } else {
        navigate('/dashboard');
      }
    }
    
    setIsSubmitting(false);
  };
  
  return (
    <FormContainer theme={theme}>
      <FormTitle theme={theme}>Log In</FormTitle>
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label theme={theme} htmlFor="email">Email</Label>
          <Input
            theme={theme}
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label theme={theme} htmlFor="password">Password</Label>
          <Input
            theme={theme}
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </FormGroup>
        <ForgotLink theme={theme}>
          <Link to="/forgot-password">Forgot password?</Link>
        </ForgotLink>
        
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Logging in...' : 'Log In'}
        </Button>
        
        {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
      </Form>
      
      <SignupLink theme={theme}>
        Don't have an account? <Link to="/register">Sign up</Link>
      </SignupLink>
    </FormContainer>
  );
};

export default LoginForm;